import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Plus, Search, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import DictableTextarea from '@/components/DictableTextarea';
import DatePickerField from '@/components/DatePickerField';

const turnoMap = { mattino: 'turno-mattino', pomeriggio: 'turno-pomeriggio', notte: 'turno-notte' };
const turnoLabels = { mattino: 'Mattino', pomeriggio: 'Pomeriggio', notte: 'Notte' };

export default function DiarioClinico() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroData, setFiltroData] = useState(new Date().toISOString().slice(0, 10));
  const [filtroTurno, setFiltroTurno] = useState('tutti');
  const [filtroOspite, setFiltroOspite] = useState('tutti');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ospite_id: '', turno: 'mattino', data: new Date().toISOString().slice(0, 10), contenuto: '', tags: '' });

  useEffect(() => {
    api.get('/ospiti', { params: { stato: 'presente' } }).then(r => setOspiti(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (filtroData) params.data = filtroData;
    if (filtroTurno !== 'tutti') params.turno = filtroTurno;
    if (filtroOspite !== 'tutti') params.ospite_id = filtroOspite;
    setLoading(true);
    api.get('/diario', { params }).then(r => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filtroData, filtroTurno, filtroOspite]);

  const ospitiMap = {};
  ospiti.forEach(o => { ospitiMap[o.id] = `${o.cognome} ${o.nome}`; });

  const handleSave = async () => {
    if (!form.ospite_id || !form.contenuto) { toast.error('Seleziona ospite e inserisci contenuto'); return; }
    try {
      await api.post('/diario', { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [] });
      toast.success('Nota aggiunta al diario');
      setShowForm(false);
      setForm({ ospite_id: '', turno: 'mattino', data: new Date().toISOString().slice(0, 10), contenuto: '', tags: '' });
      const params = {};
      if (filtroData) params.data = filtroData;
      if (filtroTurno !== 'tutti') params.turno = filtroTurno;
      api.get('/diario', { params }).then(r => setEntries(r.data));
    } catch (e) { toast.error('Errore salvataggio'); }
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="diario-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Diario Clinico</h1>
          <p className="text-sm text-slate-500 mt-0.5">Note e consegne per turno</p>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" className="gap-2" onClick={() => navigate('/giro-diario')} data-testid="avvia-giro-diario-btn">
          <Mic className="w-4 h-4" />Giro diario vocale
        </Button>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button data-testid="add-diario-global-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-2" />Nuova Nota</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuova Nota Diario</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Ospite *</Label>
                <Select value={form.ospite_id} onValueChange={v => setForm(f => ({ ...f, ospite_id: v }))}>
                  <SelectTrigger data-testid="diario-select-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                  <SelectContent>{ospiti.map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome} - {o.stanza}/{o.letto}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Turno</Label><Select value={form.turno} onValueChange={v => setForm(f => ({ ...f, turno: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mattino">Mattino</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="notte">Notte</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs">Data</Label><DatePickerField value={form.data} onChange={v => setForm(f => ({ ...f, data: v }))} /></div>
              </div>
              <div><Label className="text-xs">Contenuto *</Label><DictableTextarea data-testid="diario-global-contenuto" value={form.contenuto} onChange={e => setForm(f => ({ ...f, contenuto: e.target.value }))} rows={4} /></div>
              <div><Label className="text-xs">Tag (separati da virgola)</Label><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="alimentazione, mobilizzazione, dolore" /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-diario-global" onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white">Salva</Button></div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <DatePickerField testId="diario-filter-data" value={filtroData} onChange={v => setFiltroData(v)} className="w-44" />
        <Select value={filtroTurno} onValueChange={setFiltroTurno}>
          <SelectTrigger data-testid="diario-filter-turno" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutti">Tutti i turni</SelectItem><SelectItem value="mattino">Mattino</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="notte">Notte</SelectItem></SelectContent>
        </Select>
        <Select value={filtroOspite} onValueChange={setFiltroOspite}>
          <SelectTrigger data-testid="diario-filter-ospite" className="w-56"><SelectValue placeholder="Tutti gli ospiti" /></SelectTrigger>
          <SelectContent><SelectItem value="tutti">Tutti gli ospiti</SelectItem>{ospiti.map(o => <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Entries */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-24 p-4" /></Card>)}</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nessuna nota per i filtri selezionati</p></div>
      ) : (
        <div className="space-y-3" data-testid="diario-entries">
          {entries.map(e => (
            <Card key={e.id} className="animate-fade-in">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-slate-800">{ospitiMap[e.ospite_id] || 'Ospite'}</span>
                      <Badge className={`text-[10px] ${turnoMap[e.turno] || ''}`}>{turnoLabels[e.turno] || e.turno}</Badge>
                      <span className="text-xs text-slate-400">{new Date(e.data).toLocaleDateString('it-IT')}</span>
                    </div>
                    <p className="text-sm text-slate-700">{e.contenuto}</p>
                    {e.tags?.length > 0 && <div className="flex gap-1 mt-2">{e.tags.map((t, i) => <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>)}</div>}
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">{e.operatore_nome}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
